import { BehaviorSubject, of, forkJoin, from } from 'rxjs';
import { map, switchMap, shareReplay, finalize, distinctUntilChanged, tap, catchError, skipWhile, filter, } from 'rxjs/operators';
import { isTestEnv, mapKeyDefault, ensureMap, diffMap, syncMap } from '@onviobr/helpers';
import { interpolate } from './interpolation.js';
/**
 * Translation Manager
 * @public
 */
export class TranslationManager {
    constructor(defaultLanguage) {
        /**
         * Internal list of loaders that must get executed on each language change
         */
        this.loaders = new Set();
        /**
         * Internal list of synchronizers that must get executed on each language change and store change
         */
        this.synchronizers = new Set();
        /**
         * Internal map of loaders per language code to keep track of the ones that are already loaded in memory
         */
        this.loadedLoaders = new Map();
        /**
         * Internal map of stores per language code
         */
        this.stores = new Map();
        /**
         * Flag use to keep track of the dispose process and avoid unwanted warnings
         */
        this.disposing = false;
        /**
         * Flag used to keep track if the manager is loading content
         */
        this.loading = false;
        this._localLanguage = new BehaviorSubject(defaultLanguage);
        this.currentStore = this._localLanguage.pipe(filter(language => Boolean(language)), map(language => [language, this.getStore(language)]), switchMap(([language, store]) => {
            return this.executeAllLoaders(language, store).pipe(
            // After all loaders are executed, we need to propagate the language change to the synchronizers
            tap(() => this.synchronizers.forEach(s => s.languageChange(language))));
        }), finalize(
        /* istanbul ignore next: This is last-resort log that it's really hard to test */
        () => !this.disposing && console.error('language stream complete! This stream should not ever complete.')), shareReplay(1));
        this.currentLanguage = this.currentStore.pipe(skipWhile(() => this.loading), switchMap(() => this._localLanguage), distinctUntilChanged(), shareReplay(1));
    }
    /** {@inheritDoc TranslationManagerV1.languageAutoSync} */
    languageAutoSync(profile) {
        this.profile = profile;
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.subscription = this.profile.currentLanguage.subscribe(this._localLanguage);
    }
    /** {@inheritDoc TranslationManagerV1.isAutoSyncEnabled} */
    isAutoSyncEnabled() {
        return Boolean(this.profile);
    }
    /** {@inheritDoc TranslationManagerV1.changeLanguage} */
    changeLanguage(language) {
        if (this.profile) {
            return;
        }
        this._localLanguage.next(language);
    }
    /** {@inheritDoc TranslationManagerV1.translate} */
    translate(keys, interpolations, language) {
        if (!language) {
            return this.translateCurrent(keys, interpolations);
        }
        return this.translateSpecific(keys, language, interpolations);
    }
    /** {@inheritDoc TranslationManagerV1.addLoader} */
    addLoader(loader) {
        this.loaders.add(loader);
        this._localLanguage.next(this._localLanguage.value);
    }
    /** {@inheritDoc TranslationManagerV1.removeLoader} */
    removeLoader(loader) {
        this.loaders.delete(loader);
    }
    /** {@inheritDoc TranslationManagerV1.addSynchronizer} */
    addSynchronizer(synchronizer) {
        this.synchronizers.add(synchronizer);
    }
    /** {@inheritDoc TranslationManagerV1.removeSynchronizer} */
    removeSynchronizer(synchronizer) {
        this.synchronizers.delete(synchronizer);
    }
    /** {@inheritDoc TranslationManagerV1.dispose} */
    dispose() {
        this.disposing = true;
        this.synchronizers.clear();
        this.loaders.clear();
        this.loadedLoaders.clear();
        this.stores.clear();
        this._localLanguage.complete();
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.disposing = false;
    }
    translateCurrent(keys, interpolations) {
        return this.currentStore.pipe(skipWhile(() => this.loading), map(store => mapKeyDefault(store, keys)), map(str => interpolate(str, interpolations)), 
        // This protects the consumers in case of an internal change that does not changes their translation
        distinctUntilChanged(), 
        // This protects the manager to re-compute translations on new subscriptions
        shareReplay(1));
    }
    translateSpecific(keys, language, interpolations) {
        return of(this.getStore(language)).pipe(switchMap(store => this.executeAllLoaders(language, store)), map(store => mapKeyDefault(store, keys)), map(str => interpolate(str, interpolations)), 
        // This protects the consumers in case of an internal change that does not changes their translation
        distinctUntilChanged(), 
        // This protects the manager to re-compute translations on new subscriptions
        shareReplay(1));
    }
    getStore(language) {
        return ensureMap(this.stores, language, () => new Map());
    }
    executeAllLoaders(language, store) {
        const loaded = ensureMap(this.loadedLoaders, language, () => new WeakSet());
        const notLoaded = diffMap(this.loaders, loaded);
        if (notLoaded.length === 0) {
            return of(store);
        }
        this.loading = true;
        return of(notLoaded).pipe(switchMap(notLoaded => forkJoin(notLoaded.map(loader => this.loadersToObs(language, loader, loaded)))), map(sets => syncMap(sets, store)), tap(() => this.synchronizers.forEach(s => s.storeChange(language, store))), tap(() => {
            this.loading = false;
        }));
    }
    loadersToObs(language, loader, loaded) {
        // The use of a promise as starting point allow to defer the execution of the loader
        // and two request come really close, the use of switchMap is will prevent double execution
        return from(Promise.resolve()).pipe(switchMap(async () => loader(language)), tap(() => loaded.add(loader)), catchError(error => {
            console.error(error);
            return of({});
        }));
    }
}
/**
 * Default instance of {@link TranslationManager}
 * @remarks It will be `undefined` during testing executions to avoid unexpected network requests
 */
/* istanbul ignore next: during testing no default managers will be used */
TranslationManager.root = isTestEnv() ? undefined : new TranslationManager();
/**
 * Default {@link TranslationManager} instance
 * @public
 * @remarks It will be `undefined` during testing executions to avoid unexpected network requests
 */
export const translationManager = TranslationManager.root;
